import React, { createContext, useEffect, useState } from "react";

import { HLHLContact } from "@eave/shared/types/api";

import APIClient from "web_modules/APIClient";
import { isUserAdmin } from "web_modules/shared/session";
import Spinner from "web_modules/shared/Spinner";
import { LoadingState } from "underwriter_dashboard/shared/types";

const getContacts = async (): Promise<HLHLContact[]> => {
  if (isUserAdmin()) {
    return APIClient.get("/oa/hlhl_contacts").then(({ hlhl_contacts }) => hlhl_contacts);
  }
  return APIClient.get("/uw/hlhl_contacts").then(({ hlhl_contacts }) => hlhl_contacts);
};

const saveContact = async (contact: HLHLContact): Promise<HLHLContact> => {
  return APIClient.put("/oa/hlhl_contacts", { hlhl_contact: contact }).then(
    ({ hlhl_contact }) => hlhl_contact,
  );
};

export const HLHLContactsContext = createContext({
  hlhlContacts: [],
  // eslint-disable-next-line
  saveContact: (contact: HLHLContact): Promise<void> => Promise.resolve(),
  loading: LoadingState.INACTIVE,
});

const HLHLContactsProvider = ({ children }) => {
  const [hlhlContacts, setHlhlContacts] = useState<HLHLContact[]>([]);
  const [loading, setLoading] = useState(LoadingState.INACTIVE);

  useEffect(() => {
    const init = async () => {
      setLoading(LoadingState.ACTIVE);

      try {
        const contacts = await getContacts();
        setHlhlContacts(contacts);
      } catch (e) {
        window.alert("There was a problem initializing this page. Please contact eng.");
        throw e;
      } finally {
        setLoading(LoadingState.FINISHED);
      }
    };

    init();
  }, []);

  if (!hlhlContacts.length) {
    return <Spinner active />;
  }

  const save = async (contact: HLHLContact) => {
    setLoading(LoadingState.ACTIVE);

    try {
      const newContact = await saveContact(contact);

      let added: boolean;

      const newContactsList = hlhlContacts.map(c => {
        if (c.email === newContact.email) {
          added = true;
          return newContact;
        }
        return c;
      });

      if (!added) {
        newContactsList.push(newContact);
      }

      setHlhlContacts(newContactsList);
    } catch (e) {
      window.alert("There was a problem initializing this page. Please contact eng.");
      throw e;
    } finally {
      setLoading(LoadingState.FINISHED);
    }
  };

  return (
    <HLHLContactsContext.Provider value={{ hlhlContacts, saveContact: save, loading }}>
      {children}
    </HLHLContactsContext.Provider>
  );
};

export default HLHLContactsProvider;
